import { api } from './api';

export interface NotificationSchedule {
  id: number;
  tenantId: string;
  customerId: string;
  siteId?: string;
  reportType: string;
  frequency: string;
  cronExpression: string;
  priority: number;
  isEnabled: boolean;
  isMandatory?: boolean;
  onboardingMode?: string;
  slaMinutes: number;
  lastRunAt?: string;
  nextRunAt?: string;
  inputDataTemplate?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface NotificationReportRule {
  reportType: string;
  siteTier: string;
  isMandatory: boolean;
  isSpecialRequest: boolean;
  defaultFrequency: string;
  defaultCronExpression: string;
  defaultPriority: number;
  defaultSlaMinutes: number;
}

export interface CreateNotificationScheduleRequest {
  tenantId: string;
  customerId: string;
  siteId?: string;
  reportType: string;
  frequency: string;
  cronExpression: string;
  priority?: number;
  slaMinutes?: number;
  inputDataTemplate?: string;
}

export interface MandatoryOnboardingRequest {
  customerId: string;
  siteTier: string;
}

interface PageResponse<T> {
  content?: T[];
}

const SCHEDULE_BASE_PATH = '/report-notification/customer/api/v1/schedules';

export async function getSiteSchedules(siteId: number): Promise<NotificationSchedule[]> {
  const response = await api.get<PageResponse<NotificationSchedule>>(SCHEDULE_BASE_PATH, {
    params: { siteId, size: 200, page: 0 },
  });
  return response.data.content ?? [];
}

export async function getSiteReportRules(
  siteTier: string,
  mode: 'all' | 'mandatory' | 'special' = 'all',
): Promise<NotificationReportRule[]> {
  const response = await api.get<NotificationReportRule[]>(`${SCHEDULE_BASE_PATH}/onboarding/rules`, {
    params: { siteTier, mode },
  });
  return response.data ?? [];
}

export async function createRequestedSchedule(
  request: CreateNotificationScheduleRequest,
): Promise<NotificationSchedule> {
  const response = await api.post<NotificationSchedule>(SCHEDULE_BASE_PATH, {
    ...request,
    isMandatory: false,
    onboardingMode: 'SPECIAL',
  });
  return response.data;
}

export async function onboardMandatorySchedules(
  siteId: number,
  request: MandatoryOnboardingRequest,
): Promise<NotificationSchedule[]> {
  const response = await api.post<NotificationSchedule[]>(
    `${SCHEDULE_BASE_PATH}/onboarding/sites/${siteId}/mandatory`,
    request,
  );
  return response.data ?? [];
}

export async function enableSchedule(id: number): Promise<NotificationSchedule> {
  const response = await api.post<NotificationSchedule>(`${SCHEDULE_BASE_PATH}/${id}/enable`);
  return response.data;
}

export async function disableSchedule(id: number): Promise<NotificationSchedule> {
  const response = await api.post<NotificationSchedule>(`${SCHEDULE_BASE_PATH}/${id}/disable`);
  return response.data;
}